import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { writeFileXLSX, utils } from "xlsx";
import {
  BasicTable,
  Button,
  ButtonLink,
  DatePicker,
  ModalDefault,
  Notice,
  Skeleton,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import { maskDocument } from "../../../../helpers";
import { VALUE_SHEET_COLUMNS, normalizeValueSheetRow, parseSheetMoney, valueSheetLabel } from "../../../../helpers/valueSheet";
import {
  ImportCounts,
  ImportResultTable,
  PreviewStatusCell,
  readSheetFile,
  SheetDropZone,
  TableScroll,
} from "../../../../components/SheetImport";
import { Money, escapeHtml, formatCompetencia, formatDocument } from "@components/ow";

const REQUIRED_COLS = VALUE_SHEET_COLUMNS.filter((c) => c.required).map((c) => c.key);
// O back processa até 500 por envio
const MAX_ROWS = 500;

const STEP_UPLOAD = "upload";
const STEP_PREVIEW = "preview";
const STEP_RESULT = "result";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// Sem `position`: o `$centralize` centraliza o calendário no ancestral posicionado
// mais próximo, que assim é o modal (no campo, o calendário cobria o título)
const CompetenceField = styled.div`
  width: 240px;
`;

// As células da prévia não quebram linha: texto longo corta com reticências
const Truncate = styled.span`
  display: block;
  max-width: ${({ $width }) => $width}px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const labels = (keys) => keys.map(valueSheetLabel).join(", ");
const hasValue = (v) => v !== undefined && v !== null && v !== "";
const cellText = (value) => (hasValue(value) ? String(value) : "-");

// Mesmas regras da importação antiga; o valor em texto vale no formato brasileiro
function validateRows(data) {
  const errors = {};
  data.forEach((item, i) => {
    const rowErrs = [];
    if (!hasValue(item.contract_id)) {
      rowErrs.push("Contrato é obrigatório");
    } else if (isNaN(parseInt(item.contract_id))) {
      rowErrs.push("Contrato deve ser numérico");
    }
    const docDigits = (item.document || "").toString().replace(/\D/g, "");
    if (docDigits.length !== 11 && docDigits.length !== 14) {
      rowErrs.push("CPF/CNPJ inválido (deve ter 11 ou 14 dígitos)");
    }
    const value = parseSheetMoney(item.value);
    if (!hasValue(item.value)) {
      rowErrs.push("Valor é obrigatório");
    } else if (isNaN(value) || value <= 0) {
      rowErrs.push("Valor deve ser numérico e maior que zero");
    }
    if (rowErrs.length > 0) {
      errors[i] = rowErrs;
    }
  });
  return errors;
}

function downloadTemplate(contracts, competence) {
  const sheetData = contracts.map((c) => {
    const row = {
      contract_id: c.contract_id,
      document: maskDocument(c.document) || "",
      name: c.user_name,
      project_name: c.project_name || "-",
      value: "",
    };
    return Object.fromEntries(VALUE_SHEET_COLUMNS.map((col) => [col.label, row[col.key]]));
  });
  const ws = utils.json_to_sheet(sheetData);
  ws["!cols"] = [{ wch: 12 }, { wch: 20 }, { wch: 35 }, { wch: 25 }, { wch: 15 }];
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, "Valores variáveis");
  const monthStr = String(competence.month).padStart(2, "0");
  writeFileXLSX(wb, `valores_variaveis_${monthStr}_${competence.year}.xlsx`);
}

const PREVIEW_COLUMNS = [
  { id: "index", header: "#", size: 50, cell: ({ row }) => row.index + 1 },
  {
    id: "name",
    header: "Prestador",
    size: 220,
    cell: ({ row }) => (
      <Truncate $width={200} title={cellText(row.original.name)}>
        {cellText(row.original.name)}
      </Truncate>
    ),
  },
  { id: "document", header: "CPF/CNPJ", size: 170, cell: ({ row }) => cellText(row.original.document) },
  {
    id: "project",
    header: "Projeto",
    size: 160,
    cell: ({ row }) => (
      <Truncate $width={140} title={cellText(row.original.project_name)}>
        {cellText(row.original.project_name)}
      </Truncate>
    ),
  },
  {
    id: "value",
    header: "Valor",
    size: 130,
    align: "right",
    cell: ({ row }) => {
      const value = parseSheetMoney(row.original.value);
      return isNaN(value) ? cellText(row.original.value) : <Money value={value} />;
    },
  },
  { id: "contract", header: "Contrato", size: 90, cell: ({ row }) => cellText(row.original.contract_id) },
];

const RESULT_COLUMNS = [
  {
    id: "contractor",
    header: "Prestador",
    size: 240,
    cell: ({ row }) => (
      <Stack>
        <Text $text={escapeHtml(row.original.name || "-")} $variant="paragraph_small_bold" $color="N900" />
        {row.original.document ? (
          <Text $text={formatDocument(row.original.document)} $variant="caption_medium" $color="N400" />
        ) : null}
      </Stack>
    ),
  },
];

/**
 * Importação de valores variáveis por planilha em três passos: competência e
 * planilha, prévia e resultado. Só monta aberto. `onClose(imported)` diz se algum
 * valor entrou, para a lista recarregar.
 */
export default function ImportValuesModal({ onClose }) {
  const valueInputService = useService("contract-value-inputs");
  const now = new Date();
  const [competence, setCompetence] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  // Contratos sem valor na competência (o mesmo que vai no modelo); null enquanto carrega ou com erro
  const [template, setTemplate] = useState({ loading: true, contracts: null });
  const [step, setStep] = useState(STEP_UPLOAD);
  const [rows, setRows] = useState([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [rowErrors, setRowErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Planilha recusada: a área de soltar volta vazia (sem o nome com o ✓ verde)
  const [dropKey, setDropKey] = useState(0);
  // Só a resposta da última competência vale (trocas seguidas podem voltar fora de ordem)
  const lastRequest = useRef(0);

  const validRows = rows.filter((_, i) => !rowErrors[i]);
  const errorCount = Object.keys(rowErrors).length;
  const importCount = Math.min(validRows.length, MAX_ROWS);
  const competenceLabel = formatCompetencia(competence, true);

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competence.month, competence.year]);

  async function fetchTemplate() {
    const request = ++lastRequest.current;
    setTemplate({ loading: true, contracts: null });
    // No erro, o service já mostra o toast
    const result = await valueInputService.getTemplate(competence.month, competence.year);
    if (request !== lastRequest.current) return;
    setTemplate({ loading: false, contracts: result && !result.error ? result.data?.data || [] : null });
  }

  function close() {
    if (loading) return;
    onClose((importResult?.imported || 0) > 0);
  }

  function rejectSheet(message) {
    toast.error(message);
    setDropKey((key) => key + 1);
  }

  async function handleFile(file) {
    let json;
    try {
      json = (await readSheetFile(file)).map(normalizeValueSheetRow);
    } catch (err) {
      console.error(err);
      rejectSheet("Não foi possível ler a planilha");
      return;
    }

    if (json.length === 0) {
      rejectSheet("A planilha está vazia");
      return;
    }

    const headers = new Set(json.flatMap((row) => Object.keys(row)));
    const missingCols = REQUIRED_COLS.filter((col) => !headers.has(col));
    if (missingCols.length > 0) {
      rejectSheet(`Colunas obrigatórias ausentes: ${labels(missingCols)}`);
      return;
    }

    // Linhas sem valor ficam de fora (o modelo traz todos os contratos sem valor)
    const rowsWithValue = json.filter((row) => hasValue(row.value));
    if (rowsWithValue.length === 0) {
      rejectSheet("Nenhuma linha com valor preenchido encontrada");
      return;
    }

    setRows(rowsWithValue);
    setSkippedCount(json.length - rowsWithValue.length);
    setRowErrors(validateRows(rowsWithValue));
    setStep(STEP_PREVIEW);
  }

  function resetState() {
    setStep(STEP_UPLOAD);
    setRows([]);
    setSkippedCount(0);
    setRowErrors({});
    setImportResult(null);
  }

  async function upload() {
    setLoading(true);
    try {
      // Mesmo payload da importação antiga
      const dataToSend = validRows.slice(0, MAX_ROWS).map((item) => ({
        contract_id: parseInt(item.contract_id),
        document: (item.document || "").toString().replace(/\D/g, ""),
        value: parseSheetMoney(item.value),
      }));

      // Este método não usa o `sendError`: o toast do erro é daqui
      const res = await valueInputService.batchSubmitValues(dataToSend, competence.month, competence.year);
      if (res && !res.error && res.data) {
        setImportResult(res.data);
        setStep(STEP_RESULT);
      } else {
        toast.error(res?.msg || "Erro ao importar valores");
      }
    } catch (err) {
      toast.error("Erro ao importar valores");
    }
    setLoading(false);
  }

  let content;
  let footer;

  if (step === STEP_RESULT && importResult) {
    const hasOverflow = importResult.total_received > importResult.total_processed;
    content = (
      <Body>
        <ImportCounts
          items={[
            { count: importResult.imported, one: "importado", many: "importados" },
            { count: importResult.skipped, one: "ignorado", many: "ignorados" },
            { count: importResult.failed, one: "com falha", many: "com falha" },
          ]}
        />
        {hasOverflow ? (
          <Notice
            $variant="orange"
            $text={`Foram processadas ${importResult.total_processed} de ${importResult.total_received} linhas. Baixe uma nova planilha atualizada para importar as restantes.`}
          />
        ) : null}
        {importResult.details?.length > 0 ? <ImportResultTable details={importResult.details} columns={RESULT_COLUMNS} /> : null}
      </Body>
    );
    footer = <Button $text="Fechar" $variant="primary" $action={close} $noWidth />;
  } else if (step === STEP_PREVIEW) {
    // Status logo depois do #: as outras colunas passam da largura do modal e rolam
    const [indexColumn, ...dataColumns] = PREVIEW_COLUMNS;
    const columns = [
      indexColumn,
      { id: "status", header: "Status", size: 240, cell: ({ row }) => <PreviewStatusCell errors={rowErrors[row.index]} /> },
      ...dataColumns,
    ];
    const importLabel = `Importar ${importCount} ${importCount === 1 ? "valor" : "valores"}`;

    content = (
      <Body>
        <Stack>
          <Text $text={`Valores de ${competenceLabel}`} $variant="paragraph_small_bold" $color="N900" />
          <ImportCounts
            items={[
              { count: rows.length, one: "linha com valor", many: "linhas com valor" },
              { count: validRows.length, one: "válida", many: "válidas" },
              { count: errorCount, one: "com erro", many: "com erro", hideZero: true },
              { count: skippedCount, one: "sem valor", many: "sem valor", hideZero: true },
            ]}
          />
        </Stack>
        {errorCount > 0 ? <Notice $variant="red" $text="As linhas com erro não serão importadas." /> : null}
        {validRows.length > MAX_ROWS ? (
          <Notice
            $variant="orange"
            $text={`A planilha contém ${validRows.length} linhas válidas. Serão processadas apenas as primeiras ${MAX_ROWS}. Após o upload, baixe uma nova planilha atualizada ou remova as linhas já processadas e suba novamente.`}
          />
        ) : null}
        <TableScroll>
          <BasicTable data={rows} columns={columns} getRowId={(_, index) => String(index)} />
        </TableScroll>
      </Body>
    );
    footer = (
      <>
        <Button $text="Escolher outra planilha" $variant="outline" $action={resetState} $isDisabled={loading} $noWidth />
        <Button
          $text={importLabel}
          $variant="primary"
          $action={upload}
          $isLoading={loading}
          $isDisabled={validRows.length === 0}
          $noWidth
        />
      </>
    );
  } else {
    const pending = template.contracts?.length;
    let templateNotice = null;
    if (template.loading) {
      templateNotice = <Skeleton $height={48} $noFlex />;
    } else if (pending > 0) {
      templateNotice = (
        <Notice
          $variant="blue"
          $text={`<strong>${pending}</strong> ${pending === 1 ? "contrato variável" : "contratos variáveis"} sem valor em ${competenceLabel}.`}
        />
      );
    } else if (pending === 0) {
      templateNotice = <Notice $variant="gray" $text={`Nenhum contrato variável sem valor em ${competenceLabel}.`} />;
    }

    content = (
      <Body>
        <CompetenceField>
          <DatePicker
            $label="Competência"
            $variant="default"
            $isCompetence
            $centralize
            $date={new Date(competence.year, competence.month - 1, 1)}
            $setDate={(date) => date && setCompetence({ month: date.getMonth() + 1, year: date.getFullYear() })}
          />
        </CompetenceField>
        {templateNotice}
        <Stack>
          <Text
            $text="Campos obrigatórios: <strong>%s</strong>"
            $replaces={[labels(REQUIRED_COLS)]}
            $variant="paragraph_small_medium"
            $color="N700"
            $colorEmphasis="N900"
          />
          <Text
            $text="Baixe o modelo com os contratos sem valor na competência e preencha a coluna Valor. Linhas sem valor ficam de fora."
            $variant="paragraph_small_medium"
            $color="N700"
          />
          <Text $text={`Até ${MAX_ROWS} valores por importação.`} $variant="caption_medium" $color="N400" />
        </Stack>
        <div>
          <ButtonLink
            $text="Baixar modelo de planilha"
            $action={() => (template.contracts ? downloadTemplate(template.contracts, competence) : fetchTemplate())}
            $isDisabled={template.loading || pending === 0}
            $noPadding
            $noWidth
          />
        </div>
        <SheetDropZone key={dropKey} onFile={handleFile} />
      </Body>
    );
    footer = <Button $text="Cancelar" $variant="outline" $action={close} $noWidth />;
  }

  return (
    <ModalDefault
      $showModal
      $setShowModal={close}
      $noClose={loading}
      $size="large"
      $title="Importar valores variáveis"
      $contentBackground="N0"
      $footer={footer}
    >
      {content}
    </ModalDefault>
  );
}
